import React, { useState, useEffect } from 'react'
import { ServerConfig } from 'config'
import axios from 'axios'
import { useSelector } from 'react-redux'
import {
  Typography, Grid, Button,
  Select, MenuItem, FormControl, InputLabel
} from '@material-ui/core'

const OPERATION_TYPES = [
  { id: 0, name: 'Recycling Center (RC)' },
  { id: 1, name: 'Reverse Vending Machine (RVM)' },
  { id: 2, name: 'Curbside Program (CS)' },
  { id: 3, name: 'Drop-off Collection Program (CP)' },
  { id: 4, name: 'Community Service Program (SP)' },
  { id: 5, name: 'Bagdrop' },
]

const LogoPolicies = () => {
  const token = useSelector(state => state.auth.access_token)

  const [tenants, setTenants] = useState([])
  const [selectedTenantId, setSelectedTenantId] = useState('')

  const [policies, setPolicies] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const [selectedOpType, setSelectedOpType] = useState('')
  const [logoB64, setLogoB64] = useState('')

  const authConfig = () => ({
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })

  useEffect(() => {
    axios.get(`${ServerConfig.SERVER_URL}/admin/tenants`, authConfig())
      .then(res => setTenants(res.data))
  }, [token])

  async function fetchPolicies(tenantId) {
    setIsLoading(true)
    setError(null)
    try {
      const res = await axios.get(
        `${ServerConfig.SERVER_URL}/tenant/logo_policies?tenant_id=${tenantId}`,
        authConfig()
      )
      setPolicies(res.data)
    } catch (err) {
      setError('Failed to load logo policies.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (selectedTenantId) {
      fetchPolicies(selectedTenantId)
    } else {
      setPolicies([])
    }
    setSelectedOpType('')
    setLogoB64('')
  }, [selectedTenantId])

  function handleLogoFile(e) {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      setLogoB64(ev.target.result.split(',')[1])
    }
    reader.readAsDataURL(file)
  }

  async function createPolicy() {
    setError(null)
    try {
      await axios.post(
        `${ServerConfig.SERVER_URL}/tenant/logo_policy`,
        {
          tenant_id: selectedTenantId,
          operationtype_id: parseInt(selectedOpType),
          logo_b64: logoB64
        },
        authConfig()
      )
      setSelectedOpType('')
      setLogoB64('')
      fetchPolicies(selectedTenantId)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create logo policy.')
    }
  }

  async function deletePolicy(policy) {
    try {
      await axios.delete(`${ServerConfig.SERVER_URL}/tenant/logo_policy/${policy.id}`, authConfig())
      fetchPolicies(selectedTenantId)
    } catch (err) {
      setError('Failed to delete logo policy.')
    }
  }

  function opTypeName(id) {
    return OPERATION_TYPES.find(o => o.id === id)?.name ?? id
  }

  const usedOpTypes = new Set(policies.map(p => p.operationtype_id))
  const availableOpTypes = OPERATION_TYPES.filter(o => !usedOpTypes.has(o.id))
  const canAdd = selectedOpType !== '' && logoB64 !== ''

  const selectedTenant = tenants.find(t => t.id === selectedTenantId)

  return (
    <>
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: '20px' }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Tenant</InputLabel>
            <Select value={selectedTenantId} onChange={e => setSelectedTenantId(e.target.value)}>
              {tenants.map(tenant => (
                <MenuItem key={tenant.id} value={tenant.id}>{tenant.business_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {selectedTenant && selectedTenant.business_logo_b64 && (
          <Grid item>
            <Typography variant="caption" style={{ display: 'block' }}>Default logo</Typography>
            <img
              src={`data:image/jpeg;base64,${selectedTenant.business_logo_b64}`}
              alt="Default logo"
              style={{ maxWidth: '60px', maxHeight: '60px' }}
            />
          </Grid>
        )}
      </Grid>

      {error && (
        <Typography style={{ color: 'red', marginBottom: '10px' }}>{error}</Typography>
      )}

      {selectedTenantId && !isLoading && (
        <>
          <table className="table table-striped" style={{ width: '100%', marginBottom: '30px' }}>
            <thead>
              <tr>
                <th style={{ padding: '10px' }}>Operation Type</th>
                <th style={{ padding: '10px' }}>Logo</th>
                <th style={{ padding: '10px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {policies.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ padding: '10px', textAlign: 'center' }}>
                    No logo overrides configured for this tenant. Receipts use the default business logo.
                  </td>
                </tr>
              )}
              {policies.map(policy => (
                <tr key={policy.id}>
                  <td style={{ padding: '10px' }}>{opTypeName(policy.operationtype_id)}</td>
                  <td style={{ padding: '10px' }}>
                    <img
                      src={`data:image/jpeg;base64,${policy.logo_b64}`}
                      alt={opTypeName(policy.operationtype_id)}
                      style={{ maxWidth: '80px', maxHeight: '80px' }}
                    />
                  </td>
                  <td style={{ padding: '10px' }}>
                    <Button size="small" onClick={() => deletePolicy(policy)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Typography variant="h3" style={{ marginBottom: '15px' }}>Add Logo Policy</Typography>

          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Operation Type</InputLabel>
                <Select value={selectedOpType} onChange={e => setSelectedOpType(e.target.value)}>
                  {availableOpTypes.map(op => (
                    <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <input type="file" accept="image/*" onChange={handleLogoFile} />
              {logoB64 && (
                <img
                  src={`data:image/jpeg;base64,${logoB64}`}
                  alt="preview"
                  style={{ maxWidth: '60px', maxHeight: '60px', marginLeft: '10px' }}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={2}>
              <Button onClick={createPolicy} disabled={!canAdd}>Add</Button>
            </Grid>
          </Grid>
        </>
      )}
    </>
  )
}

export default LogoPolicies
